import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Calendar, Clock, Users, AlertTriangle, CheckCircle, Plus, Download, Filter } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

const projectTasks = [
    {
        id: 1,
        name: 'Étude de faisabilité technique',
        startDate: '2025-01-15',
        endDate: '2025-02-15',
        duration: 31,
        progress: 100,
        status: 'completed',
        assignee: 'Marie Rakoto',
        dependencies: [],
        critical: false
    },
    {
        id: 2,
        name: 'Appels d\'offres équipements',
        startDate: '2025-02-01',
        endDate: '2025-03-15',
        duration: 42,
        progress: 85,
        status: 'in_progress',
        assignee: 'Paul Andry',
        dependencies: [1],
        critical: true
    },
    {
        id: 3,
        name: 'Validation budgétaire finale',
        startDate: '2025-03-01',
        endDate: '2025-03-31',
        duration: 30,
        progress: 30,
        status: 'in_progress',
        assignee: 'Sophie Ranaivo',
        dependencies: [2],
        critical: true
    },
    {
        id: 4,
        name: 'Préparation du site',
        startDate: '2025-04-01',
        endDate: '2025-05-15',
        duration: 44,
        progress: 0,
        status: 'not_started',
        assignee: 'Jean Rakoto',
        dependencies: [3],
        critical: true
    },
    {
        id: 5,
        name: 'Installation équipements',
        startDate: '2025-05-15',
        endDate: '2025-07-30',
        duration: 76,
        progress: 0,
        status: 'not_started',
        assignee: 'Équipe technique',
        dependencies: [4],
        critical: true
    },
    {
        id: 6,
        name: 'Tests et mise en service',
        startDate: '2025-07-15',
        endDate: '2025-08-30',
        duration: 46,
        progress: 0,
        status: 'not_started',
        assignee: 'Marie Rakoto',
        dependencies: [5],
        critical: false
    }
];

const milestones = [
    { name: 'Validation technique', date: '2025-02-15', status: 'completed' },
    { name: 'Attribution marché', date: '2025-03-15', status: 'at_risk' },
    { name: 'Démarrage travaux', date: '2025-04-01', status: 'pending' },
    { name: 'Réception équipements', date: '2025-07-30', status: 'pending' },
    { name: 'Mise en production', date: '2025-08-30', status: 'pending' }
];

const resourceAllocation = [
    { name: 'Marie Rakoto', role: 'Chef de projet', workload: 80, availability: 'available' },
    { name: 'Paul Andry', role: 'Achats', workload: 60, availability: 'busy' },
    { name: 'Sophie Ranaivo', role: 'Finance', workload: 40, availability: 'available' },
    { name: 'Jean Rakoto', role: 'Technique', workload: 70, availability: 'available' },
    { name: 'Équipe technique', role: 'Installation', workload: 90, availability: 'scheduled' }
];

const statusConfig = {
    completed: { label: 'Terminé', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    in_progress: { label: 'En cours', color: 'bg-blue-100 text-blue-800', icon: Clock },
    not_started: { label: 'À venir', color: 'bg-gray-100 text-gray-800', icon: Clock },
    at_risk: { label: 'À risque', color: 'bg-red-100 text-red-800', icon: AlertTriangle }
};

const availabilityColors = {
    available: 'bg-green-100 text-green-800',
    busy: 'bg-red-100 text-red-800',
    scheduled: 'bg-yellow-100 text-yellow-800'
};

// @ts-nocheck
export default function PlanningTab({ project }) {
    const [viewMode, setViewMode] = useState('gantt');

    const calculateProjectProgress = () => {
        const totalTasks = projectTasks.length;
        const totalProgress = projectTasks.reduce((sum, task) => sum + task.progress, 0);
        return Math.round(totalProgress / totalTasks);
    };

    const getCriticalPath = () => {
        return projectTasks.filter(task => task.critical);
    };

    return (
        <div className="space-y-8">
            {/* Planning Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-50 to-teal-100">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-teal-600 mb-1">Progression globale</p>
                                <p className="text-3xl font-bold text-teal-900">{calculateProjectProgress()}%</p>
                                <Progress value={calculateProjectProgress()} className="mt-2 h-2" />
                            </div>
                            <div className="w-12 h-12 bg-teal-200 rounded-2xl flex items-center justify-center">
                                <CheckCircle className="w-6 h-6 text-teal-700" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-blue-600 mb-1">Tâches actives</p>
                                <p className="text-3xl font-bold text-blue-900">
                                    {projectTasks.filter(t => t.status === 'in_progress').length}
                                </p>
                                <p className="text-sm text-blue-600 mt-1">sur {projectTasks.length} tâches</p>
                            </div>
                            <div className="w-12 h-12 bg-blue-200 rounded-2xl flex items-center justify-center">
                                <Clock className="w-6 h-6 text-blue-700" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-orange-50 to-orange-100">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-orange-600 mb-1">Chemin critique</p>
                                <p className="text-3xl font-bold text-orange-900">
                                    {getCriticalPath().length}
                                </p>
                                <p className="text-sm text-orange-600 mt-1">tâches critiques</p>
                            </div>
                            <div className="w-12 h-12 bg-orange-200 rounded-2xl flex items-center justify-center">
                                <AlertTriangle className="w-6 h-6 text-orange-700" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-purple-100">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-purple-600 mb-1">Durée totale</p>
                                <p className="text-3xl font-bold text-purple-900">7.5</p>
                                <p className="text-sm text-purple-600 mt-1">mois estimés</p>
                            </div>
                            <div className="w-12 h-12 bg-purple-200 rounded-2xl flex items-center justify-center">
                                <Calendar className="w-6 h-6 text-purple-700" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Planning Controls */}
            <Card className="border-0 shadow-lg">
                <CardHeader>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <CardTitle className="text-xl font-bold text-gray-900">Planification projet</CardTitle>
                        <div className="flex gap-2">
                            <Button
                                variant={viewMode === 'gantt' ? 'default' : 'outline'}
                                onClick={() => setViewMode('gantt')}
                                className={viewMode === 'gantt' ? 'bg-teal-600 hover:bg-teal-700' : ''}
                            >
                                Gantt
                            </Button>
                            <Button
                                variant={viewMode === 'timeline' ? 'default' : 'outline'}
                                onClick={() => setViewMode('timeline')}
                                className={viewMode === 'timeline' ? 'bg-teal-600 hover:bg-teal-700' : ''}
                            >
                                Timeline
                            </Button>
                            <Button variant="outline" className="gap-2">
                                <Filter className="w-4 h-4" />
                                Filtres
                            </Button>
                            <Button variant="outline" className="gap-2">
                                <Download className="w-4 h-4" />
                                Exporter
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {/* Simplified Gantt Chart Representation */}
                    <div className="space-y-4">
                        <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 border-b pb-2">
                            <div className="col-span-4">Tâche</div>
                            <div className="col-span-2">Responsable</div>
                            <div className="col-span-2">Durée</div>
                            <div className="col-span-2">Progression</div>
                            <div className="col-span-2">Statut</div>
                        </div>

                        {projectTasks.map((task) => {
                            const StatusIcon = statusConfig[task.status]?.icon || Clock;
                            return (
                                <div key={task.id} className="grid grid-cols-12 gap-2 py-3 border-b border-gray-100 hover:bg-gray-50 rounded-lg px-2">
                                    <div className="col-span-4">
                                        <div className="flex items-center gap-2">
                                            {task.critical && (
                                                <div className="w-2 h-2 bg-red-500 rounded-full" title="Chemin critique" />
                                            )}
                                            <span className="font-medium text-gray-900">{task.name}</span>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {task.startDate} → {task.endDate}
                                        </p>
                                    </div>

                                    <div className="col-span-2 flex items-center">
                                        <Avatar className="w-6 h-6 mr-2">
                                            <AvatarFallback className="bg-teal-100 text-teal-700 text-xs">
                                                {task.assignee.split(' ').map(n => n[0]).join('')}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="text-sm text-gray-700">{task.assignee}</span>
                                    </div>

                                    <div className="col-span-2 flex items-center">
                                        <span className="text-sm font-medium">{task.duration} jours</span>
                                    </div>

                                    <div className="col-span-2 flex items-center">
                                        <div className="flex-1">
                                            <Progress value={task.progress} className="h-2" />
                                            <span className="text-xs text-gray-600 mt-1">{task.progress}%</span>
                                        </div>
                                    </div>

                                    <div className="col-span-2 flex items-center">
                                        <Badge className={statusConfig[task.status]?.color || 'bg-gray-100 text-gray-800'}>
                                            <StatusIcon className="w-3 h-3 mr-1" />
                                            {statusConfig[task.status]?.label || 'Inconnu'}
                                        </Badge>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Milestones and Resources */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Project Milestones */}
                <Card className="border-0 shadow-lg">
                    <CardHeader>
                        <CardTitle className="text-xl font-bold text-gray-900">Jalons du projet</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {milestones.map((milestone, index) => (
                                <div key={index} className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50">
                                    <div className={`w-4 h-4 rounded-full ${
                                        milestone.status === 'completed' ? 'bg-green-500' :
                                            milestone.status === 'at_risk' ? 'bg-red-500' : 'bg-gray-300'
                                    }`} />
                                    <div className="flex-1">
                                        <p className="font-medium text-gray-900">{milestone.name}</p>
                                        <p className="text-sm text-gray-500">{milestone.date}</p>
                                    </div>
                                    <Badge className={
                                        milestone.status === 'completed' ? 'bg-green-100 text-green-800' :
                                            milestone.status === 'at_risk' ? 'bg-red-100 text-red-800' :
                                                'bg-gray-100 text-gray-800'
                                    }>
                                        {milestone.status === 'completed' ? 'Terminé' :
                                            milestone.status === 'at_risk' ? 'À risque' : 'Planifié'}
                                    </Badge>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Resource Allocation */}
                <Card className="border-0 shadow-lg">
                    <CardHeader>
                        <CardTitle className="text-xl font-bold text-gray-900">Allocation des ressources</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {resourceAllocation.map((resource, index) => (
                                <div key={index} className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50">
                                    <Avatar className="w-10 h-10">
                                        <AvatarFallback className="bg-teal-100 text-teal-700">
                                            {resource.name.split(' ').map(n => n[0]).join('')}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1">
                                        <p className="font-medium text-gray-900">{resource.name}</p>
                                        <p className="text-sm text-gray-500">{resource.role}</p>
                                        <div className="mt-2">
                                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                                                <span>Charge de travail</span>
                                                <span>{resource.workload}%</span>
                                            </div>
                                            <Progress value={resource.workload} className="h-1.5" />
                                        </div>
                                    </div>
                                    <Badge className={availabilityColors[resource.availability]}>
                                        {resource.availability === 'available' ? 'Disponible' :
                                            resource.availability === 'busy' ? 'Occupé' : 'Planifié'}
                                    </Badge>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
